/* Personalization Studio - interactive layer.
   Markup and styles live in sections/personalization-studio.liquid.

   TIMING: the custom element registers only after the document finishes
   parsing. The add-to-cart form is in a SEPARATE section further down, so
   registering during parse means connectedCallback runs before that form
   exists and nothing can be attached to the submission.

   BUSY STATE: the button is never given the `disabled` attribute. Submission
   works by replaying the user's click once the proofs are attached, and a
   disabled button ignores .click() - so it is greyed and made unclickable with
   inline styles instead.

   CART VISIBILITY: Shopify hides any line item property whose name starts with
   an underscore from cart and checkout while still recording it on the order.

   DRY RUN: append ?psdebug=1 to the URL. */
(function () {
  var PS_DEBUG = false;
  try { PS_DEBUG = /[?&]psdebug=1/.test(window.location.search); } catch (e) {}

  /* PS_OVERSCAN: object-fit: cover leaves zero slack at zoom 1 - the image's
     own box is sized to exactly match the window, so there is nothing to pan
     until the customer zooms in first. Baking in a small permanent overscan
     means the window is always slightly over-filled, so dragging works from
     the moment a photo loads. ps-proof.js applies the identical constant so
     the printed proof matches this preview exactly - keep the two in sync. */
  var PS_OVERSCAN = 1.15;

  function reportDiag(msg, kind) {
    if (kind === 'error') { try { console.error('[Personalization Studio] ' + msg); } catch (e) {} }
    else { try { console.info('[Personalization Studio] ' + msg); } catch (e) {} }
    var boxes = document.querySelectorAll('[data-ps-diag]');
    for (var i = 0; i < boxes.length; i++) {
      boxes[i].textContent = msg;
      boxes[i].className = 'ps-diag' + (kind ? ' is-' + kind : '');
    }
  }

  try {
    class PersonalizationStudio extends HTMLElement {
      connectedCallback() {
        try { this.init(); }
        catch (err) { reportDiag('INIT FAILED: ' + (err && err.message ? err.message : String(err)), 'error'); }
      }

      /* NOTE: never assign to `prefix`, `form`, `accept`, `title`, `id`, `slot`,
         `dataset` or `hidden` on `this` - they are real DOM properties on Element
         and several are getter-only, which throws in a class body (strict mode). */
      init() {
        var self = this;
        this.propPrefix = this.dataset.propertyPrefix || '';
        this.maxBytes = (parseFloat(this.dataset.maxFileMb) || 15) * 1024 * 1024;
        this.acceptTypes = (this.dataset.accept || '').split(',');
        this.acceptLabel = this.dataset.acceptLabel || 'JPG or PNG';
        this.objectUrls = [];
        this.photoState = {};
        this.stageInner = this.querySelector('[data-ps-inner]');
        this.readyToSubmit = false;

        var cfg = document.querySelector('[data-ps-cart-detail]');
        this.cartDetail = cfg ? cfg.getAttribute('data-ps-cart-detail') : 'words';
        this.proofPrefix = cfg ? (cfg.getAttribute('data-ps-proof-prefix') || '') : '';

        this.cartForm = this.findForm();

        var specEl = this.querySelector('[data-ps-spec]');
        try { this.spec = specEl ? JSON.parse(specEl.textContent) : null; }
        catch (e) { this.spec = null; }

        // One id per visit, shared by both faces so a pair is obvious in a folder.
        this.orderTag = String(Math.floor(1000 + Math.random() * 9000));

        var texts = this.querySelectorAll('[data-ps-input]');
        var filesIn = this.querySelectorAll('[data-ps-file]');
        var wins = this.querySelectorAll('[data-ps-window]');
        var flips = this.querySelectorAll('[data-ps-flip]');

        texts.forEach(function (input) {
          input.addEventListener('input', function () { self.guard(function () { self.onText(input); }); });
          self.guard(function () { self.onText(input); });
        });
        filesIn.forEach(function (input) {
          input.addEventListener('change', function () { self.guard(function () { self.onFile(input); }); });
          self.wireDropzone(input);
        });
        this.querySelectorAll('[data-ps-clear]').forEach(function (btn) {
          btn.addEventListener('click', function () { self.guard(function () { self.clearFile(btn.getAttribute('data-ps-clear')); }); });
        });
        this.querySelectorAll('[data-ps-zoom]').forEach(function (slider) {
          slider.addEventListener('input', function () {
            self.guard(function () {
              var id = slider.getAttribute('data-ps-zoom');
              self.state(id).zoom = parseInt(slider.value, 10) / 100;
              self.applyPhoto(id);
            });
          });
        });
        wins.forEach(function (win) { self.wireDrag(win); });
        flips.forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            self.guard(function () { self.flip(btn.getAttribute('data-ps-flip')); });
          });
        });

        /* Auto-flip to whichever side someone starts working on, so the
           preview always shows what they're typing without hunting for the
           Front/Back toggle - moving from a back field to fix their name up
           front flips it back too. Delegated on the element itself so it
           also covers fields shown later by a back-layout switch. */
        this.addEventListener('focusin', function (e) {
          var field = e.target && e.target.closest && e.target.closest('[data-ps-fieldface]');
          if (field) self.guard(function () { self.flip(field.getAttribute('data-ps-fieldface') === 'back' ? 'back' : 'front'); });
        });

        /* Foil pickers (pattern / foil color / phrase). Shows the matching foil
           layers over the photo and records the choices as cart properties. */
        this.querySelectorAll('[data-ps-foil-choice]').forEach(function (r) {
          r.addEventListener('change', function () { self.guard(function () { self.applyFoil(); }); });
        });
        this.guard(function () { self.applyFoil(); });

        this.bindForm();

        reportDiag(
          (PS_DEBUG ? 'DRY RUN - nothing will be added to the cart. ' : '') +
          'Ready. text fields: ' + texts.length +
          ', photo fields: ' + filesIn.length +
          ', cart form: ' + (this.cartForm ? 'found' : 'NOT FOUND') +
          ', submit buttons: ' + this.boundButtons +
          ', cart detail: ' + this.cartDetail +
          ', proof name: ' + this.proofName('front') +
          ', proof renderer: ' + (window.PSProof ? 'loaded' : 'MISSING'),
          this.cartForm ? 'ok' : 'error'
        );
      }

      proofName(face) {
        var base = this.proofPrefix || (this.spec && this.spec.fileBase) || 'riqueco_proof';
        return base + '_' + this.orderTag + '_' + face + '.jpg';
      }

      bindForm() {
        var self = this;
        this.boundButtons = this.boundButtons || 0;
        if (!this.cartForm) this.cartForm = this.findForm();
        if (!this.cartForm) return;

        var buttons = this.cartForm.querySelectorAll('button[type="submit"], input[type="submit"], button[name="add"]');
        buttons.forEach(function (btn) {
          if (btn.dataset.psBound) return;
          btn.dataset.psBound = '1';
          btn.addEventListener('click', function (e) { self.onAddClick(e, btn); }, true);
          self.boundButtons++;
        });
        if (!this.cartForm.dataset.psBound) {
          this.cartForm.dataset.psBound = '1';
          this.cartForm.addEventListener('submit', function (e) { self.onSubmit(e); }, true);
        }
        this.querySelectorAll('[data-ps-input]').forEach(function (input) {
          self.guard(function () { self.onText(input); });
        });
        this.guard(function () { self.applyFoil(); });
      }

      /* ---------- foil options ---------- */
      foilSelection() {
        var sel = {};
        this.querySelectorAll('[data-ps-foil-choice]').forEach(function (r) {
          if (r.checked) sel[r.getAttribute('data-ps-foil-choice')] = r.value;
        });
        return sel;
      }

      applyFoil() {
        var self = this;
        var groups = {};
        this.querySelectorAll('[data-ps-foil-choice]').forEach(function (r) { groups[r.getAttribute('data-ps-foil-choice')] = true; });
        Object.keys(groups).forEach(function (g) {
          var radios = self.querySelectorAll('[data-ps-foil-choice="' + g + '"]');
          var any = false;
          radios.forEach(function (r) { if (r.checked) any = true; });
          if (!any && radios.length) radios[0].checked = true;
          var on = null;
          radios.forEach(function (r) {
            var wrap = r.closest('label');
            if (wrap) wrap.classList.toggle('is-on', r.checked);
            if (r.checked) on = r;
          });
          var box = self.querySelector('[data-ps-foil-group="' + g + '"]');
          var groupLabel = box ? (box.getAttribute('data-ps-foil-grouplabel') || g) : g;
          self.syncHidden('foil-' + g, groupLabel, on ? on.getAttribute('data-ps-foil-label') : '', 'text');
        });
        var sel = this.foilSelection();
        this.querySelectorAll('[data-ps-foil-layer]').forEach(function (img) {
          var show = ['pattern', 'foil', 'phrase'].every(function (g) {
            var need = img.getAttribute('data-' + g);
            return !need || need === sel[g];
          });
          if (show && !img.getAttribute('src')) img.setAttribute('src', img.getAttribute('data-src'));
          img.hidden = !show;
        });
      }

      guard(fn) {
        try { fn(); } catch (err) {
          reportDiag('RUNTIME ERROR: ' + (err && err.message ? err.message : String(err)), 'error');
        }
      }

      disconnectedCallback() { (this.objectUrls || []).forEach(function (u) { URL.revokeObjectURL(u); }); }

      findForm() {
        var forms = document.querySelectorAll('form[action*="/cart/add"]');
        for (var i = 0; i < forms.length; i++) { if (forms[i].querySelector('[name="id"]')) return forms[i]; }
        return forms[0] || null;
      }

      /* kind: 'text' (their wording) | 'tech' (file, crop, dimensions) | 'proof' */
      propName(label, kind) {
        var hide = false;
        if (kind === 'tech') hide = this.cartDetail !== 'all';
        if (kind === 'text') hide = this.cartDetail === 'proofs';
        var base = (this.propPrefix ? this.propPrefix + ' ' : '') + label;
        return 'properties[' + (hide ? '_' : '') + base + ']';
      }

      state(id) {
        if (!this.photoState[id]) this.photoState[id] = { zoom: 1, x: 0, y: 0 };
        return this.photoState[id];
      }

      flip(face) {
        if (!this.stageInner) return;
        if (face === 'back') this.stageInner.classList.add('is-flipped');
        else this.stageInner.classList.remove('is-flipped');
        this.querySelectorAll('[data-ps-flip]').forEach(function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-ps-flip') === face ? 'true' : 'false');
        });
      }

      onText(input) {
        var id = input.getAttribute('data-ps-input');
        var count = this.querySelector('[data-ps-count="' + id + '"]');
        var raw = input.value;
        var upper = input.getAttribute('data-ps-upper') === 'true';
        /* EVERY empty field shows its placeholder as on-card ghost text -
           "Your Message Here", "Caption 1", etc. - required or not, so the
           mock-up always shows where that detail will land before anyone
           starts typing. This is a live-preview affordance only: the actual
           printed proof never reads this ghost text. buildProofs() below
           collects values straight from input.value, and ps-proof.js treats
           a blank/whitespace value as nothing to draw - so a caption or
           message left empty by the customer still prints truly blank,
           exactly as before. Only the on-screen mock-up changed. */
        var shown = upper ? raw.toUpperCase() : raw;
        var fallback = input.placeholder || '';
        if (upper) fallback = fallback.toUpperCase();

        if (count) count.textContent = String(raw.length);

        var layer = this.querySelector('[data-ps-layer="' + id + '"]');
        if (layer) {
          layer.textContent = raw.trim() ? raw : fallback;
          layer.classList.toggle('ps-empty', !raw.trim());
        }

        var curve = this.querySelector('[data-ps-curve="' + id + '"]');
        if (curve) {
          var tp = curve.querySelector('textPath');
          if (tp) tp.textContent = raw.trim() ? shown : fallback;
          curve.classList.toggle('ps-empty', !raw.trim());
        }

        this.syncHidden(id, input.getAttribute('data-ps-label'), raw, 'text');
        this.clearError(id);
      }

      syncHidden(id, label, value, kind) {
        if (!this.cartForm) return;
        var hidden = this.cartForm.querySelector('input[data-ps-hidden="' + id + '"]');
        if (!value || !String(value).trim()) { if (hidden) hidden.parentNode.removeChild(hidden); return; }
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.setAttribute('data-ps-hidden', id);
          this.cartForm.appendChild(hidden);
        }
        hidden.name = this.propName(label, kind || 'text');
        hidden.value = value;
      }

      wireDropzone(input) {
        var self = this;
        var id = input.getAttribute('data-ps-file');
        var drop = this.querySelector('[data-ps-drop="' + id + '"]');
        if (!drop) return;
        ['dragenter', 'dragover'].forEach(function (ev) {
          drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.add('is-drag'); });
        });
        ['dragleave', 'drop'].forEach(function (ev) {
          drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.remove('is-drag'); });
        });
        drop.addEventListener('drop', function (e) {
          if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
            input.files = e.dataTransfer.files;
            self.guard(function () { self.onFile(input); });
          }
        });
      }

      wireDrag(win) {
        var self = this;
        var id = win.getAttribute('data-ps-window');
        var dragging = false, startX = 0, startY = 0, originX = 0, originY = 0;
        win.addEventListener('pointerdown', function (e) {
          if (!win.classList.contains('is-loaded')) return;
          dragging = true;
          win.classList.add('is-dragging');
          win.setPointerCapture(e.pointerId);
          var st = self.state(id);
          startX = e.clientX; startY = e.clientY; originX = st.x; originY = st.y;
          e.preventDefault();
        });
        win.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          var rect = win.getBoundingClientRect();
          var st = self.state(id);
          st.x = originX + ((e.clientX - startX) / rect.width) * 100;
          st.y = originY + ((e.clientY - startY) / rect.height) * 100;
          self.applyPhoto(id);
        });
        ['pointerup', 'pointercancel'].forEach(function (ev) {
          win.addEventListener(ev, function () { dragging = false; win.classList.remove('is-dragging'); });
        });
      }

      applyPhoto(id) {
        var img = this.querySelector('[data-ps-photo="' + id + '"]');
        if (!img) return;
        var st = this.state(id);
        var effZoom = st.zoom * PS_OVERSCAN;
        var limit = ((effZoom - 1) / 2) * 100;
        if (st.x > limit) st.x = limit;
        if (st.x < -limit) st.x = -limit;
        if (st.y > limit) st.y = limit;
        if (st.y < -limit) st.y = -limit;
        img.style.transform = 'translate(' + st.x.toFixed(2) + '%, ' + st.y.toFixed(2) + '%) scale(' + effZoom.toFixed(3) + ')';

        var input = document.querySelector('[data-ps-file="' + id + '"]');
        if (input && input.files && input.files.length) {
          this.syncHidden(id + '-placement', input.getAttribute('data-ps-label') + ' placement',
            'zoom ' + st.zoom.toFixed(2) + 'x, offset ' + st.x.toFixed(1) + '% / ' + st.y.toFixed(1) + '%', 'tech');
        }
      }

      warnBox(id) { return this.querySelector('[data-ps-warn="' + id + '"]'); }

      showWarn(id, title, body, info) {
        var box = this.warnBox(id);
        if (!box) return;
        box.innerHTML = '';
        var s = document.createElement('strong');
        s.textContent = title;
        box.appendChild(s);
        box.appendChild(document.createTextNode(body));
        box.classList.toggle('ps-warn--info', !!info);
        box.hidden = false;
      }

      hideWarn(id) {
        var box = this.warnBox(id);
        if (box) { box.hidden = true; box.innerHTML = ''; }
      }

      checkQuality(id, input, file, w, h) {
        var minW = parseInt(input.getAttribute('data-ps-min-w'), 10) || 0;
        var minH = parseInt(input.getAttribute('data-ps-min-h'), 10) || 0;
        var minKB = parseInt(input.getAttribute('data-ps-min-kb'), 10) || 0;
        var label = input.getAttribute('data-ps-label');

        var dims = this.querySelector('[data-ps-tools="' + id + '"] [data-ps-filedims]');
        var mb = (file.size / 1048576).toFixed(2);
        if (dims) dims.textContent = w + ' x ' + h + ' px, ' + mb + ' MB';

        var small = (minW && w < minW) || (minH && h < minH);
        var light = minKB && file.size < minKB * 1024;

        if (small || light) {
          var reason = small
            ? 'This photo is ' + w + ' x ' + h + ' pixels. We recommend at least ' + minW + ' x ' + minH + '.'
            : 'This file is ' + mb + ' MB. We recommend at least ' + (minKB / 1000).toFixed(1).replace('.0', '') + ' MB, since heavily compressed photos lose detail on press.';
          this.showWarn(id, 'This photo may print soft',
            reason + ' You can still order - we will look at it closely when we prepare your proof and tell you honestly how it will look. If you have a larger original, that is always the better one to send.');
        } else {
          this.hideWarn(id);
        }

        this.syncHidden(id + '-meta', label + ' file',
          w + ' x ' + h + ' px, ' + mb + ' MB' + ((small || light) ? ' - BELOW RECOMMENDED, customer warned' : ''), 'tech');
      }

      onFile(input) {
        var self = this;
        var id = input.getAttribute('data-ps-file');
        var file = input.files && input.files[0];
        var win = this.querySelector('[data-ps-window="' + id + '"]');
        var img = this.querySelector('[data-ps-photo="' + id + '"]');
        var hint = this.querySelector('[data-ps-hint="' + id + '"]');
        var tools = this.querySelector('[data-ps-tools="' + id + '"]');

        if (!file) { this.clearFile(id); return; }

        if (this.acceptTypes.length && file.type && this.acceptTypes.indexOf(file.type) === -1) {
          this.clearFile(id);
          this.showError(id, 'Please upload a ' + this.acceptLabel + ' file. If your photo is a HEIC from an iPhone, open it and choose Export or Duplicate as JPEG first.');
          return;
        }

        if (file.size > this.maxBytes) {
          this.clearFile(id);
          this.showError(id, 'That photo is too large. Please keep it under ' + this.dataset.maxFileMb + 'MB.');
          return;
        }

        this.hideWarn(id);

        if (tools) {
          tools.hidden = false;
          var t = tools.querySelector('[data-ps-filename-text]');
          if (t) t.textContent = file.name;
          var d = tools.querySelector('[data-ps-filedims]');
          if (d) d.textContent = '';
        }

        var url = URL.createObjectURL(file);
        this.objectUrls.push(url);

        if (img) {
          img.onload = function () {
            self.guard(function () { self.checkQuality(id, input, file, img.naturalWidth, img.naturalHeight); });
          };
          img.onerror = function () {
            self.clearFile(id);
            self.showError(id, 'We could not read that image. Please try re-saving it as a JPG or PNG and uploading again.');
          };
          img.src = url;
          img.hidden = false;
        }

        if (hint) hint.style.display = 'none';
        if (win) win.classList.add('is-loaded');

        this.photoState[id] = { zoom: 1, x: 0, y: 0 };
        var slider = this.querySelector('[data-ps-zoom="' + id + '"]');
        if (slider) slider.value = 100;
        this.applyPhoto(id);

        if (!this.cartForm) this.bindForm();
        if (this.cartForm) {
          input.name = this.propName(input.getAttribute('data-ps-label'), 'tech');
          if (input.parentElement !== this.cartForm) {
            /* Moving the input out of its .ps-drop label drops it out from
               under the CSS rule that hides it there, so the browser's own
               "Choose File" control was appearing bare in the add-to-cart
               form - one per photo slot the customer had filled in. It has
               already done its job by this point (the file is attached), so
               it can be hidden outright rather than restyled. */
            input.style.display = 'none';
            this.cartForm.appendChild(input);
          }
          this.cartForm.setAttribute('enctype', 'multipart/form-data');
        }
        this.clearError(id);
      }

      clearFile(id) {
        var input = document.querySelector('[data-ps-file="' + id + '"]');
        var win = this.querySelector('[data-ps-window="' + id + '"]');
        var img = this.querySelector('[data-ps-photo="' + id + '"]');
        var hint = this.querySelector('[data-ps-hint="' + id + '"]');
        var tools = this.querySelector('[data-ps-tools="' + id + '"]');
        var drop = this.querySelector('[data-ps-drop="' + id + '"]');
        if (input) {
          input.value = '';
          input.removeAttribute('name');
          input.style.display = '';
          if (drop && input.parentElement !== drop) drop.appendChild(input);
        }
        if (img) { img.hidden = true; img.onload = null; img.onerror = null; img.removeAttribute('src'); }
        if (hint) hint.style.display = '';
        if (win) win.classList.remove('is-loaded');
        if (tools) tools.hidden = true;
        this.hideWarn(id);
        this.photoState[id] = { zoom: 1, x: 0, y: 0 };
        this.syncHidden(id + '-placement', 'placement', '', 'tech');
        this.syncHidden(id + '-meta', 'file', '', 'tech');
      }

      showError(id, message) {
        var field = this.querySelector('[data-ps-field="' + id + '"]');
        if (!field) return;
        field.classList.add('has-error');
        if (message) {
          var el = field.querySelector('.ps-error');
          if (el) el.textContent = message;
        }
        this.flip(field.getAttribute('data-ps-fieldface') === 'back' ? 'back' : 'front');
      }

      clearError(id) {
        var field = this.querySelector('[data-ps-field="' + id + '"]');
        if (field) field.classList.remove('has-error');
      }

      validate() {
        var self = this, ok = true, first = null;
        this.querySelectorAll('[data-ps-input]').forEach(function (input) {
          if (input.getAttribute('data-ps-required') === 'true' && !input.value.trim()) {
            if (!first) first = input;
            ok = false;
            self.showError(input.getAttribute('data-ps-input'), 'Please fill this in before adding to cart.');
          }
        });
        document.querySelectorAll('[data-ps-file]').forEach(function (input) {
          if (input.getAttribute('data-ps-required') === 'true' && !(input.files && input.files.length)) {
            if (!first) first = input;
            ok = false;
            self.showError(input.getAttribute('data-ps-file'), 'Please upload a photo before adding to cart.');
          }
        });
        if (!ok && first && first.scrollIntoView) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return ok;
      }

      /* ---------- proof ---------- */
      submitButton() {
        return this.cartForm ? this.cartForm.querySelector('button[type="submit"], input[type="submit"], button[name="add"]') : null;
      }

      /* Deliberately does NOT set `disabled` - see the note at the top of this file. */
      setBusy(on, message) {
        var btn = this.submitButton();
        if (!btn) return;
        if (on) {
          if (!btn.dataset.psLabel) btn.dataset.psLabel = btn.innerHTML;
          btn.innerHTML = message || 'Preparing your proof...';
          btn.style.pointerEvents = 'none';
          btn.style.opacity = '0.65';
          btn.style.cursor = 'wait';
          btn.setAttribute('aria-busy', 'true');
        } else {
          if (btn.dataset.psLabel) btn.innerHTML = btn.dataset.psLabel;
          btn.style.pointerEvents = '';
          btn.style.opacity = '';
          btn.style.cursor = '';
          btn.setAttribute('aria-busy', 'false');
        }
      }

      describeForm() {
        if (!this.cartForm) return 'no form';
        var out = [];
        this.cartForm.querySelectorAll('[name^="properties"]').forEach(function (i) {
          var key = i.name.replace('properties[', '').replace(']', '');
          if (i.type === 'file') out.push(key + ' = FILE:' + (i.files && i.files.length ? i.files[0].name + ' ' + Math.round(i.files[0].size / 1024) + 'KB' : 'EMPTY'));
          else out.push(key + ' = "' + String(i.value).slice(0, 40) + '"');
        });
        return out.length ? out.join('  |  ') : 'NO PROPERTIES ON FORM';
      }

      attachProof(face, blob) {
        if (!this.cartForm || !blob) return false;
        var label = face === 'back' ? 'Proof - back' : 'Proof - front';
        var input = this.cartForm.querySelector('input[data-ps-proof="' + face + '"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'file';
          input.setAttribute('data-ps-proof', face);
          input.style.display = 'none';
          this.cartForm.appendChild(input);
        }
        input.name = this.propName(label, 'proof');
        try {
          var dt = new DataTransfer();
          dt.items.add(new File([blob], this.proofName(face), { type: 'image/jpeg' }));
          input.files = dt.files;
          return true;
        } catch (e) {
          reportDiag('PROOF ATTACH FAILED (DataTransfer unsupported): ' + e.message, 'error');
          return false;
        }
      }

      buildProofs() {
        var self = this;
        if (!this.spec) { reportDiag('NO PROOF: spec missing from page.', 'error'); return Promise.resolve(); }
        if (!window.PSProof) { reportDiag('NO PROOF: ps-proof.js did not load.', 'error'); return Promise.resolve(); }

        var values = {};
        this.querySelectorAll('[data-ps-input]').forEach(function (i) {
          values[i.getAttribute('data-ps-input')] = i.value;
        });

        var photos = {};
        this.querySelectorAll('[data-ps-photo]').forEach(function (img) {
          var id = img.getAttribute('data-ps-photo');
          if (img.getAttribute('src') && img.naturalWidth) photos[id] = { img: img, state: self.state(id) };
        });

        var proofSpec = {};
        Object.keys(this.spec).forEach(function (k) { proofSpec[k] = self.spec[k]; });
        proofSpec.foilSel = this.foilSelection();

        var work = window.PSProof.render(proofSpec, values, photos, 1200)
          .then(function (results) {
            var attached = 0;
            results.forEach(function (r) { if (self.attachProof(r.face, r.blob)) attached++; });
            if (self.cartForm) self.cartForm.setAttribute('enctype', 'multipart/form-data');
            var err = window.PSProof.lastError();
            reportDiag(
              'PROOFS: ' + attached + ' attached' + (err ? ' | ERROR: ' + err : '') +
              '  ||  SENDING: ' + self.describeForm(),
              attached > 0 ? 'ok' : 'error'
            );
          })
          .catch(function (e) {
            reportDiag('PROOF RENDER FAILED: ' + (e && e.message ? e.message : String(e)) +
              '  ||  SENDING: ' + self.describeForm(), 'error');
          });

        return Promise.race([work, new Promise(function (r) { setTimeout(r, 8000); })]);
      }

      finish(go) {
        var self = this;
        if (PS_DEBUG) {
          this.setBusy(false);
          var box = document.querySelector('[data-ps-diag]');
          if (box && box.scrollIntoView) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.readyToSubmit = true;
        this.setBusy(true, 'Adding to cart...');
        go();
        setTimeout(function () { self.readyToSubmit = false; self.setBusy(false); }, 6000);
      }

      onAddClick(event, btn) {
        if (this.readyToSubmit) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.validate()) return;
        var self = this;
        this.setBusy(true, 'Preparing your proof...');
        this.buildProofs().then(function () { self.finish(function () { btn.click(); }); });
      }

      onSubmit(event) {
        if (this.readyToSubmit) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.validate()) return;
        var self = this;
        this.setBusy(true, 'Preparing your proof...');
        this.buildProofs().then(function () {
          self.finish(function () {
            if (self.cartForm.requestSubmit) self.cartForm.requestSubmit();
            else HTMLFormElement.prototype.submit.call(self.cartForm);
          });
        });
      }
    }

    function boot() {
      if (customElements.get('personalization-studio')) return;
      customElements.define('personalization-studio', PersonalizationStudio);
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  } catch (err) {
    reportDiag('SCRIPT THREW BEFORE SETUP: ' + (err && err.message ? err.message : String(err)), 'error');
  }
})();